import { Link, createFileRoute, redirect, useRouter } from '@tanstack/react-router'
import { z } from 'zod'
import { Calculator } from 'lucide-react'
import { LoginForm } from '@/features/auth/login-form'
import { ensureSession } from '@/lib/api'
import { needsTwoFactorEnrollment } from '@/lib/auth'
import { ROUTES } from '@/lib/routes'

/** Parámetros de búsqueda del login: a dónde volver tras entrar. */
const loginSearchSchema = z.object({
  redirect: z.string().optional().catch(undefined),
})

/** Solo se aceptan rutas internas como destino de retorno (evita redirecciones abiertas). */
function safeRedirect(target: string | undefined): string | null {
  if (!target) return null
  try {
    const url = new URL(target, window.location.origin)
    if (url.origin !== window.location.origin || url.pathname.startsWith(ROUTES.login)) return null
    return url.pathname + url.search + url.hash
  } catch {
    return null
  }
}

export const Route = createFileRoute('/_auth/login')({
  validateSearch: loginSearchSchema,
  beforeLoad: async () => {
    if (await ensureSession()) throw redirect({ to: ROUTES.dashboard })
  },
  component: LoginPage,
})

function LoginPage() {
  const router = useRouter()
  const search = Route.useSearch()
  return (
    <>
      <div className="rounded-2xl border border-border bg-card p-6 shadow-[0_8px_40px_hsl(var(--shadow-color)/0.08)] sm:p-8">
        <LoginForm
          onSuccess={(session) => {
            if (session.employee.mustChangePassword) {
              void router.navigate({ to: ROUTES.changePassword })
              return
            }
            if (needsTwoFactorEnrollment(session.employee)) {
              void router.navigate({ to: ROUTES.twoFactorSetup })
              return
            }
            const target = safeRedirect(search.redirect)
            // El destino viene de la URL: se navega por historial, sin construir `to` a mano.
            if (target) router.history.push(target)
            else void router.navigate({ to: ROUTES.dashboard })
          }}
        />
      </div>
      <p className="mt-6 text-center text-sm text-muted-foreground">
        <Link to={ROUTES.simulator} className="inline-flex items-center gap-1.5 font-medium text-primary hover:underline">
          <Calculator className="size-4" aria-hidden /> Simular un crédito
        </Link>
      </p>
    </>
  )
}
